/* ============================================================================
   Tour do professor — FILTRO E NUMERAÇÃO
   ============================================================================
   Funções PURAS. `alvoExiste` entra por parâmetro em vez de esta camada chamar
   `document.querySelector` direto, e é o que torna toda a regra testável sem
   DOM, sem React e sem Joyride — o mesmo padrão de TrilhaLudica/nucleo.

   A regra que justifica o arquivo: no AVA o menu vem do backend e varia por
   cliente, então o tour precisa se montar a partir do que EXISTE naquela tela,
   e não do que a gente escreveu em passos.js.
   ========================================================================== */

/**
 * Descarta os passos que não têm como acontecer neste cliente.
 *
 * Duas passadas, e a ordem entre elas importa:
 *
 * 1. ausência do alvo — só para `verificarNoInicio`, que são os itens de menu.
 *    Alvo de tela interna não é avaliado aqui: ele legitimamente ainda não
 *    existe no DOM quando o tour abre (quem cuida deles é `aguardarAlvo`).
 * 2. órfãos — passo cujo `dependeDe` caiu na passada 1 não tem como ser
 *    alcançado, porque o passo que levava até aquela tela não existe mais. Roda
 *    em cascata: se A cai, B que depende de A cai, e C que depende de B também.
 *
 * @param {Array}    passos
 * @param {Function} alvoExiste  recebe o PASSO INTEIRO, não só o seletor: a
 *                               busca por nome visível (`alvoPorTexto`) precisa
 *                               valer já aqui. Filtrar só pelo href descartaria,
 *                               antes de qualquer tentativa, o passo cujo
 *                               caminho no backend não é o que supomos — foi o
 *                               que sumiu com Conteúdos e ADR.
 * @returns {Array} nova lista (não muta a entrada)
 */
export function filtrarPassos(passos, alvoExiste) {
  const presentes = passos.filter(
    passo => !passo.verificarNoInicio || alvoExiste(passo)
  );

  return removerOrfaos(presentes);
}

/**
 * Remove em cascata os passos cuja dependência não está mais na lista.
 *
 * O laço repete até estabilizar porque a queda de um passo pode orfanar outro
 * que já tinha passado pela verificação — uma passada só resolveria apenas o
 * primeiro nível da corrente.
 */
function removerOrfaos(passos) {
  let atual = passos;

  for (;;) {
    const ids = new Set(atual.map(passo => passo.id));
    const filtrado = atual.filter(
      passo => !passo.dependeDe || ids.has(passo.dependeDe)
    );

    if (filtrado.length === atual.length) return filtrado;
    atual = filtrado;
  }
}

/**
 * Para onde ir quando o passo `indicePulado` não pôde ser mostrado.
 *
 * A cascata de `dependeDe` do `filtrarPassos` acontece **na abertura do tour**, e
 * resolve o alvo que já se sabe ausente. Esta aqui é a mesma ideia em TEMPO DE
 * EXECUÇÃO, para o alvo que só se descobre ausente na hora — tipicamente porque
 * depende de DADO, e não de menu.
 *
 * O caso que motivou: no tour do adm escolar, quatro passos apontam para botões de
 * LINHA da listagem de turmas (editar, importar, membros, estatísticas). Numa
 * escola sem turma nenhuma não existe linha, e sem cascata cada um esperaria o seu
 * timeout de 8s antes de ser pulado — meio minuto de tour parado. Com ela, o
 * primeiro que falha leva os outros três junto.
 *
 * @returns {number} índice do próximo passo que não depende do que foi pulado
 */
export function proximoApos(passos, indicePulado) {
  const pulados = new Set([passos[indicePulado].id]);

  for (let i = indicePulado + 1; i < passos.length; i += 1) {
    if (passos[i].dependeDe && pulados.has(passos[i].dependeDe)) {
      pulados.add(passos[i].id);
      continue;
    }

    return i;
  }

  return passos.length;
}

/**
 * Numera os passos DEPOIS do filtro.
 *
 * Numerar antes seria anunciar ao professor passos que nunca vão chegar — "3 de
 * 10" num tour que na verdade tem 5 no cliente dele.
 *
 * @returns {Array} cada passo com `indice` (1-based) e `total`
 */
export function numerar(passos) {
  return passos.map((passo, i) => ({
    ...passo,
    indice: i + 1,
    total: passos.length
  }));
}

/**
 * `filtrarPassos` + `numerar`, que é sempre como os dois são usados.
 */
export function prepararPassos(passos, alvoExiste) {
  return numerar(filtrarPassos(passos, alvoExiste));
}

/**
 * Traduz um passo nosso para o formato que o Joyride espera.
 *
 * A tradução vive aqui, e não no componente, para que o formato do balão seja
 * testável e para que trocar o Joyride por um componente próprio custe só o
 * `index.jsx` (o encaixe do overlay com os modais do CoreUI é o risco conhecido
 * desta dependência).
 *
 * `disableBeacon` em todos: beacon é o pontinho que o Joyride mostra esperando
 * um clique antes de abrir o balão. Num tour pedido de propósito, ele só
 * adiciona um clique a cada passo.
 *
 * Em passo de clique guiado o rodapé sai inteiro: quem avança é a navegação
 * real do professor, então um "Próximo" ali seria um segundo caminho para a
 * mesma coisa — e ensinaria justamente o caminho errado.
 */
/**
 * Onde o balão vai, considerando a largura da tela.
 *
 * Passo de menu pede `right`, e no desktop é o certo. Em tela estreita a sidebar
 * do CoreUI vira overlay: com ~256px dela numa tela de 455px sobram ~200px ao
 * lado, e o balão tem `maxWidth` de 300px — não cabe, e é o que fazia o balão
 * aparecer solto com a seta apontando para fora da tela.
 *
 * `bottom` é a troca certa porque o item de menu está sempre na coluna da
 * esquerda: embaixo dele há a largura toda da tela.
 *
 * `center` nunca é trocado — é o passo de boas-vindas e o de encerramento, que não
 * apontam para nada.
 */
export function posicaoEfetiva(passo, estreito = false) {
  const posicao = passo.posicao || "auto";

  if (!estreito || posicao === "center") return posicao;
  if (posicao === "right" || posicao === "left") return "bottom";

  return posicao;
}

export function paraJoyride(passo, elementoResolvido = null, opcoes = {}) {
  // Passo guiado esconde o rodapé porque quem avança é a navegação. MAS se a
  // pessoa JÁ ESTÁ na tela que o passo pedia, não existe navegação para
  // acontecer: o clique no item destacado não muda rota, `componentDidUpdate`
  // nunca dispara, e sem rodapé o tour fica parado para sempre. Era o que
  // travava o tour do professor aberto de dentro da listagem de atividades.
  const cliqueGuiado = Boolean(passo.rotaEsperada) && !opcoes.jaEstaNaRota;

  return {
    placement: posicaoEfetiva(passo, opcoes.estreito),
    // O Joyride aceita seletor OU elemento. Passamos o ELEMENTO quando o alvo foi
    // achado pelo nome visível, porque nesse caso não existe seletor que o
    // descreva. Quando o próprio seletor casou, mandamos o seletor: assim o
    // Joyride reconsulta o DOM a cada render e não guarda referência a um nó que
    // o Sidebar pode ter substituído.
    //
    // `body` como último recurso porque há passo SEM seletor nenhum — item de
    // menu que só se acha pelo nome, como "Atividades" (href dinâmico) e o
    // cabeçalho de "Conteúdos" (seção com filhos não tem href). Enquanto o passo
    // não é o atual, ele ainda não foi resolvido, e um `target: undefined` faria o
    // Joyride reclamar de passo inválido na lista.
    target: elementoResolvido || passo.alvo || "body",
    title: passo.titulo,
    content: passo.texto,
    disableBeacon: true,
    hideFooter: cliqueGuiado,
    spotlightClicks: Boolean(passo.permiteCliqueNoAlvo),
    // Passo centralizado não tem elemento para recortar; sem isto o Joyride
    // desenha um furo no canto superior esquerdo da tela.
    disableOverlay: false,
    styles: passo.posicao === "center" ? { spotlight: { display: "none" } } : {}
  };
}
